import { useCallback } from 'react';

import { queuePromptDraft, requestPromptDraft } from 'src/components/ai/chat/chat-state';
import { aiThreadIdVar } from 'src/editor/toolbars/right-panel/ai';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';

/**
 * Opens the chat drawer with a prompt in its input, for the user to finish
 * and send. Starts a new thread: a run or a generation is its own piece of
 * work. `keepOpenThread` drafts into the thread already open, and starts a
 * chat only when the drawer has none.
 */
export function useDraftPromptInNewChat(keepOpenThread = false): (prompt: string) => void {
  const { openDrawer } = useGlobalDrawer();

  return useCallback(
    (prompt: string) => {
      const openThreadId = aiThreadIdVar();
      if (keepOpenThread && openThreadId) {
        openDrawer();
        requestPromptDraft(prompt);
        return;
      }

      /**
       * Leaving a thread, the draft is queued rather than written through the
       * live input: that input still belongs to the old thread until the
       * panel changes scope and registers the new one.
       */
      const isLeavingExistingThread = Boolean(openThreadId);
      aiThreadIdVar(null);
      openDrawer();
      if (isLeavingExistingThread) queuePromptDraft(prompt);
      else requestPromptDraft(prompt);
    },
    [keepOpenThread, openDrawer]
  );
}

/** Drafts into the open thread. Starts a chat only when none is open. */
export function useDraftPromptInOpenThread(): (prompt: string) => void {
  return useDraftPromptInNewChat(true);
}
